import { useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { api, imgUrl } from "@/lib/api";
import ImageField from "@/components/admin/ImageField";

const inputCls = "w-full rounded-xl border border-line bg-white px-3.5 py-2.5 text-sm outline-none focus:border-srs";

export default function GalleryTab() {
  const qc = useQueryClient();
  const [items, setItems] = useState(null);
  const [mode, setMode] = useState("photo");
  const [form, setForm] = useState({ title: "", tag: "", image: "", before: "", after: "" });

  const load = () => api.get("/gallery").then(({ data }) => setItems(data));
  useEffect(() => {
    load();
  }, []);

  if (!items) return <p className="font-mono text-xs uppercase tracking-[0.25em] text-subtle">Loading…</p>;

  const add = async () => {
    if (mode === "photo" && !form.image) return toast.error("Add a photo first.");
    if (mode === "pair" && (!form.before || !form.after)) return toast.error("Add both before and after photos.");
    await api.post("/admin/gallery", { type: mode, title: form.title, tag: form.tag, image: form.image, before: form.before, after: form.after });
    setForm({ title: "", tag: "", image: "", before: "", after: "" });
    toast.success("Gallery updated.");
    load();
    qc.invalidateQueries({ queryKey: ["site"] });
  };

  const del = async (g) => {
    if (!window.confirm("Remove this item?")) return;
    await api.delete(`/admin/gallery/${g.id}`);
    load();
    qc.invalidateQueries({ queryKey: ["site"] });
  };

  return (
    <div className="max-w-3xl">
      <div className="mb-8 rounded-3xl border border-line bg-white p-5 shadow-[0_14px_35px_-24px_rgba(13,13,17,0.18)]">
        <div className="mb-4 flex gap-2">
          {["photo", "pair"].map((m) => (
            <button
              key={m}
              data-testid={`admin-gallery-mode-${m}`}
              onClick={() => setMode(m)}
              className={`px-4 py-2 font-mono text-[10px] uppercase tracking-[0.18em] ${mode === m ? "bg-ink text-white" : "border border-line text-mute"}`}
            >
              {m === "photo" ? "Single photo" : "Before / After"}
            </button>
          ))}
        </div>
        <div className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <input className={inputCls} placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="admin-gallery-title-input" />
            {mode === "photo" && (
              <input className={inputCls} placeholder="Tag (e.g. Roofing)" value={form.tag} onChange={(e) => setForm({ ...form, tag: e.target.value })} />
            )}
          </div>
          {mode === "photo" ? (
            <ImageField value={form.image} onChange={(v) => setForm({ ...form, image: v })} testid="admin-gallery-image" />
          ) : (
            <div className="grid gap-4 sm:grid-cols-2">
              <ImageField label="Before" value={form.before} onChange={(v) => setForm({ ...form, before: v })} testid="admin-gallery-before" />
              <ImageField label="After" value={form.after} onChange={(v) => setForm({ ...form, after: v })} testid="admin-gallery-after" />
            </div>
          )}
          <button data-testid="admin-gallery-add-button" onClick={add} className="flex items-center gap-2 bg-ink px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.18em] text-white hover:bg-srs">
            <Plus size={14} /> Add to gallery
          </button>
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2" data-testid="admin-gallery-list">
        {items.map((g, i) => (
          <div key={g.id} className="border border-line bg-white p-4" data-testid={`admin-gallery-row-${i}`}>
            <div className="flex gap-2">
              {g.type === "pair" ? (
                <>
                  <img src={imgUrl(g.before)} alt="" className="h-20 w-1/2 object-cover" />
                  <img src={imgUrl(g.after)} alt="" className="h-20 w-1/2 object-cover" />
                </>
              ) : (
                <img src={imgUrl(g.image)} alt="" className="h-20 w-full object-cover" />
              )}
            </div>
            <div className="mt-3 flex items-center justify-between">
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-ink">{g.title || "Untitled"}</p>
                <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-subtle">{g.type === "pair" ? "Before / After" : g.tag || "Photo"}</p>
              </div>
              <button onClick={() => del(g)} className="p-2 text-mute hover:text-srs" aria-label="Delete"><Trash2 size={15} /></button>
            </div>
          </div>
        ))}
        {!items.length && <p className="p-4 text-sm text-subtle sm:col-span-2">No gallery items yet.</p>}
      </div>
    </div>
  );
}
